import { useReducer, useRef, useCallback } from 'react'

// Snapshots are opaque to this hook. CanvasEditor stores lightweight per-sheet
// Fabric state together with source image configuration; URLs and Fabric internals
// remain outside history.

function undoReducer(state, action) {
  switch (action.type) {
    case 'SNAPSHOT': {
      const trimmed = state.history.slice(0, state.cursor + 1)
      const next = [...trimmed, action.payload].slice(-action.maxHistory)
      return { history: next, cursor: next.length - 1 }
    }
    case 'CAPTURE_BASELINE': {
      if (state.cursor < 0) return { history: [action.payload], cursor: 0 }

      const next = state.history.slice(0, state.cursor + 1)
      next[state.cursor] = action.payload
      return { history: next, cursor: state.cursor }
    }
    case 'RESET':
      return { history: [action.payload], cursor: 0 }
    case 'UNDO':
      return state.cursor > 0
        ? { ...state, cursor: state.cursor - 1 }
        : state
    case 'REDO':
      return state.cursor < state.history.length - 1
        ? { ...state, cursor: state.cursor + 1 }
        : state
    default:
      return state
  }
}

export function useUndoRedo(getState, applyState, maxHistory = 50) {
  const [{ history, cursor }, dispatch] = useReducer(undoReducer, { history: [], cursor: -1 })
  const isRestoringRef = useRef(false)

  const snapshot = useCallback(() => {
    if (isRestoringRef.current) return  // block snapshots triggered during applyState
    dispatch({ type: 'SNAPSHOT', payload: getState(), maxHistory })
  }, [getState, maxHistory])

  const undo = useCallback(() => {
    if (cursor <= 0) return
    if (isRestoringRef.current) return  // prevent concurrent restores from rapid keypresses
    isRestoringRef.current = true
    // Pass onComplete: ref is lowered and cursor dispatched INSIDE the callback,
    // after all async source.clone() calls in applyState have resolved.
    applyState(history[cursor - 1], () => {
      dispatch({ type: 'UNDO' })
      isRestoringRef.current = false
    })
  }, [cursor, history, applyState])

  const redo = useCallback(() => {
    if (cursor >= history.length - 1) return
    if (isRestoringRef.current) return  // prevent concurrent restores from rapid keypresses
    isRestoringRef.current = true
    applyState(history[cursor + 1], () => {
      dispatch({ type: 'REDO' })
      isRestoringRef.current = false
    })
  }, [cursor, history, applyState])

  const reset = useCallback(() => {
    isRestoringRef.current = false
    dispatch({ type: 'RESET', payload: getState() })
  }, [getState])

  // startAtomicBatch(fn):
  //   Suppresses snapshot() during the operation and produces exactly one undo entry when done.
  //   fn receives an `onComplete(commit = true)` callback and calls it after async work resolves.
  //   Passing false releases the batch without creating a completed-operation snapshot.
  const startAtomicBatch = useCallback((fn) => {
    if (isRestoringRef.current) return false  // don't nest batches
    // Source configuration can change without a Fabric event. Refresh the current
    // entry before the operation so one undo restores both source and canvas state.
    dispatch({ type: 'CAPTURE_BASELINE', payload: getState() })
    isRestoringRef.current = true
    let completed = false
    const complete = (commit = true) => {
      if (completed) return
      completed = true
      isRestoringRef.current = false
      if (commit) snapshot()
    }
    try {
      fn(complete)
    } catch (error) {
      complete(false)
      throw error
    }
    return true
  }, [getState, snapshot])

  return { snapshot, undo, redo, reset, canUndo: cursor > 0, canRedo: cursor < history.length - 1, startAtomicBatch }
}
